import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Crop, getCrops } from '@next/arable-lib/crop';
import { CropField } from '@next/arable-lib/crop-field';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, switchMap, take, tap } from 'rxjs';

import { SiteState } from 'src/app/shared/models';
import { UnsavedChangesService } from 'src/app/shared/services/unsaved-changes.service';
import { CropYearService } from 'src/app/shared/services/crop-year.service';
import { selectCropYear } from 'src/app/store/selectors';

import {
  ProcessedAnswers,
  ProcessedAnswersWithForm,
  SoilQuestions,
  SoilQuestionsRequest,
  TgaCropFieldWithStatus,
} from 'src/app/tga/shared/models/models';
import { fetchSoilQuestions, loadCropFields, postSoilQuestions } from 'src/app/tga/store/tga.actions';
import { selectCropFieldById, selectSoilQuestions } from 'src/app/tga/store/tga.selectors';

import { SpecificSoilQuestionnaireService } from './specific-soil-questionnaire.service';
import {
  DepthStorendeLaag,
  GrasslandOrGrassSeed,
  MainSoilTillageMethod,
  MaterialState,
  NematodeLaboraties,
  Structuurbederfsource,
  TgaInputType,
  TGAQuestion,
} from '../../../models/question-models';
import { SpecificQuestionnaireComponent } from '../shared/specific-questionnaire/specific-questionnaire.component';
import { TranslatePipe } from '@next/core-lib/i18n';

@Component({
  selector: 'app-specific-soil-questionnaire',
  templateUrl: './specific-soil-questionnaire.component.html',
  imports: [SpecificQuestionnaireComponent, TranslatePipe],
})
export class SpecificSoilQuestionnaireComponent implements OnInit {
  protected readonly isCropYearReadOnly = inject(CropYearService).isReadOnly;
  private readonly store = inject(Store<SiteState>);
  private readonly questionService = inject(SpecificSoilQuestionnaireService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  readonly cropFieldId = signal<number | null>(null);
  readonly cropYear = signal<number | null>(null);
  readonly cropField = signal<CropField | undefined>(undefined);
  readonly soilQuestions = signal<TGAQuestion<TgaInputType>[]>([]);
  readonly crops = signal<Crop[]>([]);
  readonly soilQuestions$ = this.store.select(selectSoilQuestions);
  private readonly selectedCropYear$ = this.store.select(selectCropYear);
  readonly updateFormState = (questions: TGAQuestion<TgaInputType>[], formGroup: FormGroup): void => {
    this.questionService.updateAmAnalysisPercentageState(questions, formGroup);
  };

  readonly greenManureCrops$ = this.store.select(getCrops).pipe(
    map((crop) => {
      // Create a shallow copy to avoid mutating the store data, then sort alphabetically
      const sortedCrops = [...crop].sort((a, b) => {
        if (!a.name || !b.name) {
          return 0;
        }
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      });

      // Filter green manure crops
      const greenManureCropfields = sortedCrops
        .filter((c) => c.isGreenManure)
        .map((c) => ({
          key: c.id.toString(),
          value: c.name,
        }));

      // This crop is used to represent 'unknown' green manure crops in the questionnaire
      const unknownGreenManureCrop = sortedCrops.find((c) => c.agrifirmCrop?.id === 1015);
      if (unknownGreenManureCrop) {
        greenManureCropfields.push({
          key: unknownGreenManureCrop.id.toString(),
          value: unknownGreenManureCrop.name,
        });
      }

      return greenManureCropfields;
    }),
  );

  private readonly params$ = this.route.params.pipe(
    takeUntilDestroyed(this.destroyRef),
    map(this.questionService.extractRouteParams.bind(this.questionService)),
    filter(this.questionService.validateRouteParams.bind(this.questionService)),
    tap(({ cropFieldId, cropYear }) => {
      this.cropFieldId.set(cropFieldId);
      this.cropYear.set(cropYear);
    }),
  ) as ReturnType<typeof this.route.params.pipe<{ cropFieldId: number; cropYear: number }>>;

  private readonly cropField$ = this.params$.pipe(
    switchMap((params) => this.store.select(selectCropFieldById(params.cropFieldId))),
    tap(this.handleCropFieldResult.bind(this)),
  );

  private readonly questions$ = combineLatest([
    this.greenManureCrops$.pipe(filter((cropField) => cropField !== null && cropField.length > 0)),
    this.cropField$.pipe(filter((cropField) => cropField !== null)),
    this.soilQuestions$,
  ]).pipe(
    tap(([greenManureCrops]) => {
      this.questionService.initializeSoilQuestions(greenManureCrops);
    }),
    map(() => {
      return this.questionService.initValues();
    }),
  );

  ngOnInit(): void {
    this.store
      .select(getCrops)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((crops) => this.crops.set(crops || []));

    this.params$
      .pipe(
        switchMap((params) =>
          this.questions$.pipe(
            take(1),
            tap((questions) => this.checkAndFetchMissingQuestions(questions, params)),
          ),
        ),
      )
      .subscribe();

    this.questions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((questions) => questions.length > 0),
      )
      .subscribe((questions) => {
        this.soilQuestions.set(questions);
      });
  }

  private handleCropFieldResult(cropfieldWithStatus: TgaCropFieldWithStatus | null) {
    const cropYear = this.cropYear();
    if (!cropfieldWithStatus && cropYear !== null) {
      this.selectedCropYear$.pipe(take(1)).subscribe((selectedCropYear) => {
        this.store.dispatch(loadCropFields({ cropYear: selectedCropYear }));
      });
    }
    this.cropField.set(cropfieldWithStatus?.cropField ?? undefined);
  }

  private checkAndFetchMissingQuestions(
    _questions: TGAQuestion<TgaInputType>[],
    params: { cropFieldId: number; cropYear: number },
  ) {
    this.selectedCropYear$.pipe(take(1)).subscribe((cropYear) => {
      this.store.dispatch(
        fetchSoilQuestions({
          cropFieldId: params.cropFieldId,
          cropYear,
        }),
      );
    });
  }

  answersUpdated(data: ProcessedAnswersWithForm): void {
    if (this.isCropYearReadOnly()) return;

    const { answers: processedAnswers, formGroup } = data;
    this.unsavedChangesService.registerForm(formGroup);

    const fieldId = this.cropFieldId();
    const year = this.cropYear();

    if (fieldId && year) {
      const soilQuestions: SoilQuestions = {
        isRentedField: this.questionService.processYesNoAnswer(
          processedAnswers[SpecificSoilQuestionnaireService.isRentedField],
        ),
        lastHarvestedYear: this.questionService.processDateYearAnswer(
          processedAnswers[SpecificSoilQuestionnaireService.lastHarvestedYear] as unknown as Date,
        ),
        mainSoilTillageMethod:
          MainSoilTillageMethod[
          processedAnswers[
          SpecificSoilQuestionnaireService.mainSoilTillageMethod
          ] as keyof typeof MainSoilTillageMethod
          ],
        isInLuwte: this.questionService.processYesNoAnswer(
          processedAnswers[SpecificSoilQuestionnaireService.isInLuwte],
        ),
        hadGrasslandOrGrassSeed:
          GrasslandOrGrassSeed[
          processedAnswers[
          SpecificSoilQuestionnaireService.hadGrasslandOrGrassSeed
          ] as keyof typeof GrasslandOrGrassSeed
          ],
        greenManureCropId: this.questionService.processGreenManureCropId(
          processedAnswers[SpecificSoilQuestionnaireService.greenManureCropId],
        ),

        ...this.questionService.processDiseasesOnThePlot(processedAnswers),

        hasSoilVariety: this.questionService.processYesNoUnknownAnswer(
          processedAnswers[SpecificSoilQuestionnaireService.hasSoilVariety],
        ),
        soilVarietyDifferences: processedAnswers[SpecificSoilQuestionnaireService.soilVarietyDifferences] as string,
        isStuifgevoelig: this.questionService.processYesNoAnswer(
          processedAnswers[SpecificSoilQuestionnaireService.IsStuifgevoelig],
        ),
        hasSoilScan: this.questionService.processYesNoAnswer(
          processedAnswers[SpecificSoilQuestionnaireService.hasSoilscan],
        ),

        structuurbederfSource:
          Structuurbederfsource[
          processedAnswers[
          SpecificSoilQuestionnaireService.structuurbederfsource
          ] as keyof typeof Structuurbederfsource
          ],

        depthStorendeLaag:
          DepthStorendeLaag[
          processedAnswers[SpecificSoilQuestionnaireService.depthStorendeLaag] as keyof typeof DepthStorendeLaag
          ],

        isBeredenUnderWetConditions: this.questionService.processYesNoAnswer(
          processedAnswers[SpecificSoilQuestionnaireService.isBeredenUnderWetConditions],
        ),
        hasWorkingDrainage: this.questionService.processYesNoAnswer(
          processedAnswers[SpecificSoilQuestionnaireService.hasWorkingDrainage],
        ),
        hasLaagten: this.questionService.processYesNoAnswer(
          processedAnswers[SpecificSoilQuestionnaireService.hasLaagten],
        ),

        knolbehandelingMaterial:
          MaterialState[
          processedAnswers[SpecificSoilQuestionnaireService.knolbehandelingMaterial] as keyof typeof MaterialState
          ],

        ...this.questionService.processIsExpectedInUitgangsmateriaal(
          processedAnswers[SpecificSoilQuestionnaireService.isExpectedInUitgangsmateriaal],
        ),

        ...this.processAmAnalysisFields(processedAnswers),

        ...this.processVrijlevendAnalysisFields(processedAnswers),

        // Unused fields, send them for now
        hasIrrigation: false,
        hasProefkuil: false,
        cropRotation: [],
        totalYearsWithGrassGrainCorn: 0,
        totalYearsWithGrassGrainCorn_IsManual: false,
      };

      const answersPayload: SoilQuestionsRequest = {
        ...soilQuestions,
        cropFieldId: fieldId,
        cropYear: new Date().getFullYear(),
      };
      this.store.dispatch(postSoilQuestions({ answers: answersPayload }));

      this.unsavedChangesService.markFormCompleted();
    }
  }

  private processAmAnalysisFields(
    processedAnswers: ProcessedAnswers,
  ): Pick<
    SoilQuestions,
    | 'amAnalysisHasAmAnalysis'
    | 'amAnalysisAmSampleYear'
    | 'amAnalysisLaboratory'
    | 'amAnalysisTotalLevendeLarvenEieren'
    | 'amAnalysisGRostochiensisPercentage'
    | 'amAnalysisGPallidaPercentage'
  > {
    const hasAmAnalysis = this.questionService.processYesNoAnswer(
      processedAnswers[SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis],
    );

    if (!hasAmAnalysis) {
      return {
        amAnalysisHasAmAnalysis: hasAmAnalysis,
        amAnalysisAmSampleYear: null,
        amAnalysisLaboratory: null,
        amAnalysisTotalLevendeLarvenEieren: null,
        amAnalysisGRostochiensisPercentage: null,
        amAnalysisGPallidaPercentage: null,
      };
    }

    const totalLevendeLarvenEieren = processedAnswers[
      SpecificSoilQuestionnaireService.amAnalysisTotalLevendeLarvenEieren
    ] as number;
    const shouldClearPercentages = totalLevendeLarvenEieren === 0;

    return {
      amAnalysisHasAmAnalysis: hasAmAnalysis,
      amAnalysisAmSampleYear: this.questionService.processDateYearAnswer(
        processedAnswers[SpecificSoilQuestionnaireService.amAnalysisAmSampleYear] as unknown as Date,
      ),
      amAnalysisTotalLevendeLarvenEieren: totalLevendeLarvenEieren,
      amAnalysisLaboratory:
        NematodeLaboraties[
        processedAnswers[SpecificSoilQuestionnaireService.amAnalysisLaboratory] as keyof typeof NematodeLaboraties
        ],
      amAnalysisGRostochiensisPercentage: shouldClearPercentages
        ? null
        : (processedAnswers[SpecificSoilQuestionnaireService.amAnalysisGRostochiensisPercentage] as number),
      amAnalysisGPallidaPercentage: shouldClearPercentages
        ? null
        : (processedAnswers[SpecificSoilQuestionnaireService.amAnalysisGPallidaPercentage] as number),
    };
  }

  private processVrijlevendAnalysisFields(
    processedAnswers: ProcessedAnswers,
  ): Pick<
    SoilQuestions,
    | 'vrijlevendAnalysisHasAaltjesAnalysis'
    | 'vrijlevendAnalysisSampleYear'
    | 'vrijlevendAnalysisDitylenchusDestructor'
    | 'vrijlevendAnalysisDitylenchusDipsaci'
    | 'vrijlevendAnalysisParatrichodorusPachydermus'
    | 'vrijlevendAnalysisParatrichodorusTeres'
    | 'vrijlevendAnalysisTrichodorusPrimitivus'
    | 'vrijlevendAnalysisTrichodorusSimilis'
    | 'vrijlevendAnalysisMeloidogyneChitwoodi'
    | 'vrijlevendAnalysisMeloidogyneFallax'
    | 'vrijlevendAnalysisMeloidogyneHapla'
    | 'vrijlevendAnalysisPratylenchusPenetrans'
  > {
    const hasAaltjesAnalysis = this.questionService.processYesNoAnswer(
      processedAnswers[SpecificSoilQuestionnaireService.vrijlevendAnalysisHasAaltjesAnalysis],
    );

    if (!hasAaltjesAnalysis) {
      return {
        vrijlevendAnalysisHasAaltjesAnalysis: false,
        vrijlevendAnalysisSampleYear: null,
        vrijlevendAnalysisDitylenchusDestructor: null,
        vrijlevendAnalysisDitylenchusDipsaci: null,
        vrijlevendAnalysisParatrichodorusPachydermus: null,
        vrijlevendAnalysisParatrichodorusTeres: null,
        vrijlevendAnalysisTrichodorusPrimitivus: null,
        vrijlevendAnalysisTrichodorusSimilis: null,
        vrijlevendAnalysisMeloidogyneChitwoodi: null,
        vrijlevendAnalysisMeloidogyneFallax: null,
        vrijlevendAnalysisMeloidogyneHapla: null,
        vrijlevendAnalysisPratylenchusPenetrans: null,
      };
    }

    return {
      vrijlevendAnalysisHasAaltjesAnalysis: hasAaltjesAnalysis,
      vrijlevendAnalysisSampleYear: this.questionService.processDateYearAnswer(
        processedAnswers[SpecificSoilQuestionnaireService.vrijlevendAnalysisSampleYear] as unknown as Date,
      ),
      vrijlevendAnalysisDitylenchusDestructor: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisDitylenchusDestructor
      ] as number,
      vrijlevendAnalysisDitylenchusDipsaci: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisDitylenchusDipsaci
      ] as number,
      vrijlevendAnalysisParatrichodorusPachydermus: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisParatrichodorusPachydermus
      ] as number,
      vrijlevendAnalysisParatrichodorusTeres: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisParatrichodorusTeres
      ] as number,
      vrijlevendAnalysisTrichodorusPrimitivus: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisTrichodorusPrimitivus
      ] as number,
      vrijlevendAnalysisTrichodorusSimilis: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisTrichodorusSimilis
      ] as number,
      vrijlevendAnalysisMeloidogyneChitwoodi: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisMeloidogyneChitwoodi
      ] as number,
      vrijlevendAnalysisMeloidogyneFallax: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisMeloidogyneFallax
      ] as number,
      vrijlevendAnalysisMeloidogyneHapla: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisMeloidogyneHapla
      ] as number,
      vrijlevendAnalysisPratylenchusPenetrans: processedAnswers[
        SpecificSoilQuestionnaireService.vrijlevendAnalysisPratylenchusPenetrans
      ] as number,
    };
  }
}
